import React, { Fragment, useState, useEffect } from 'react';
import { Link, withPrefix, graphql } from 'gatsby';
import styled from 'styled-components';
import Seo from 'components/seo';
import BlogSidebar from 'components/BlogSidebar';
import { isRunningInAppWebView } from 'common/components/AppModeDetector';
import AppAwareLink from 'components/BlogSidebar/AppAwareLink';
import ContentPage from 'layouts/ContentPage';
import SiteLayout from 'layouts/SiteLayout';

const BlogPageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 2.5rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  width: 100%;
`;

const BlogPost = styled.article`
  margin-bottom: 20px;
  padding: 24px;
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-radius: 16px;
  background: ${props => props.theme.colors.surface};
  
  &:last-child {
    border-bottom: none;
  }
`;

const PostMeta = styled.div`
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textColor};
  opacity: 0.8;
`;

const PostDate = styled.time`
  font-weight: 500;
`;

const PostCategory = styled.span`
  display: inline-block;
  text-transform: uppercase;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.secondaryDark};
`;

const PostTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  font-weight: 700;
  line-height: 1.3;
  color: ${props => props.theme.colors.headingColor};
  
  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const PostImageContainer = styled.div`
  margin-bottom: 1.5rem;
  border-radius: 12px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const PostExcerpt = styled.div`
  margin-bottom: 1.2rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.textColor};
`;

const ReadMoreButton = styled(Link)`
  && {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
    padding: 10px 16px 10px 18px;
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.white};
    -webkit-text-fill-color: ${props => props.theme.colors.white};
    text-decoration: none;
    border-radius: 999px;
    font-weight: 700;
    font-size: 14px;
    line-height: 1.2;
  }

  &&::after {
    content: '→';
    color: ${props => props.theme.colors.secondary};
    font-size: 16px;
    line-height: 1;
  }

  &&:hover,
  &&:focus-visible {
    background: ${props => props.theme.colors.primaryHover};
    color: ${props => props.theme.colors.white};
    text-decoration: none;
  }
`;

const BackToBlogs = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &:before {
    content: "←";
    margin-right: 8px;
  }
`;

// Define this content component before CategoryTemplate
const BlogListContent = ({ data, pageContext }) => {
  const { edges: posts } = data.allMarkdownRemark;
  const { category } = pageContext;
  
  // Process image path to handle both static and CMS paths
  const processImagePath = (path) => {
    if (!path) return null;
    
    // If path starts with '/', it's relative to the static folder
    if (typeof path === 'string' && path.startsWith('/')) {
      return withPrefix(path);
    }
    
    return path;
  };
  
  return (
    <BlogPageContainer>
      <MainContent>
        <PostCategory>{category}</PostCategory>
        
        {posts.map(({ node }) => {
          const title = node.frontmatter.title || node.fields.slug;
          const thumbnailPath = processImagePath(node.frontmatter.thumbnail);
            
          return (
            <BlogPost key={node.fields.slug}>
              <PostCategory>
                {category}
              </PostCategory>
              
              <PostTitle>
                <AppAwareLink to={node.fields.slug}>{title}</AppAwareLink>
              </PostTitle>
              
              <PostMeta>
                <PostDate>{node.frontmatter.date}</PostDate>
              </PostMeta>
              
              {thumbnailPath && (
                <PostImageContainer>
                  <AppAwareLink to={node.fields.slug}>
                    <img 
                      src={thumbnailPath}
                      alt={title}
                    />
                  </AppAwareLink>
                </PostImageContainer>
              )}
              
              <PostExcerpt>
                {node.frontmatter.description || node.excerpt}
              </PostExcerpt>
              
              <ReadMoreButton as={AppAwareLink} to={node.fields.slug}>
                Read more
              </ReadMoreButton>
            </BlogPost>
          );
        })}
      </MainContent>
    </BlogPageContainer>
  );
};

const CategoryTemplate = ({ data, pageContext, location }) => {
  // Use state for the combined value, not just the app detection
  const [shouldHideHeaderFooter, setShouldHideHeaderFooter] = useState(false);
  
  // Check URL parameters for app mode - highest priority
  const isAppMode = location && location.search ? 
    new URLSearchParams(location.search).get('app') === 'true' : false;
  
  useEffect(() => {
    // If app=true parameter is present, apply immediately to avoid flicker
    if (isAppMode) {
      setShouldHideHeaderFooter(true);
      return;
    }
    
    // Only check for WebView detection if not already determined by URL parameter
    if (typeof window !== 'undefined') {
      setShouldHideHeaderFooter(isRunningInAppWebView());
    }
  }, [isAppMode]);
  
  const { edges: posts } = data.allMarkdownRemark;
  const { category } = pageContext;
  
  // Process image path to handle both static and CMS paths
  const processImagePath = (path) => {
    if (!path) return null;
    
    // If path starts with '/', it's relative to the static folder
    // Use withPrefix to get the correct URL in both dev and production
    if (typeof path === 'string' && path.startsWith('/')) {
      return withPrefix(path);
    }
    
    return path;
  };

  // Conditionally render based on app mode
  if (shouldHideHeaderFooter) {
    return (
      <Fragment>
        <div className="app-view" style={{ padding: 0, margin: 0 }}>
          <BlogListContent data={data} pageContext={pageContext} />
        </div>
      </Fragment>
    );
  }

  return (
    <SiteLayout>
        <Seo 
          title={`${category}`} 
          description={`Browse all posts about ${category} on the Furbuz Blog`} 
          isBlogPage={true}
        />
        
        <ContentPage
          title={category}
          description={`Browse all articles in the ${category} category`}
          eyebrow="Blog category"
          width="1200px"
        >
          <BlogPageContainer>
            <MainContent>
              <BackToBlogs to="/blog">Back to all blogs</BackToBlogs>
              
              {posts.map(({ node }) => {
                const title = node.frontmatter.title || node.fields.slug;
                const thumbnailPath = processImagePath(node.frontmatter.thumbnail);
                  
                return (
                  <BlogPost key={node.fields.slug}>
                    <PostCategory>
                      {category}
                    </PostCategory>
                    
                    <PostTitle>
                      <AppAwareLink to={node.fields.slug}>{title}</AppAwareLink>
                    </PostTitle>
                    
                    <PostMeta>
                      <PostDate>{node.frontmatter.date}</PostDate>
                    </PostMeta>
                    
                    {thumbnailPath && (
                      <PostImageContainer>
                        <AppAwareLink to={node.fields.slug}>
                          <img 
                            src={thumbnailPath}
                            alt={title}
                          />
                        </AppAwareLink>
                      </PostImageContainer>
                    )}
                    
                    <PostExcerpt>
                      {node.frontmatter.description || node.excerpt}
                    </PostExcerpt>
                    
                    <ReadMoreButton to={node.fields.slug}>
                      Read more
                    </ReadMoreButton>
                  </BlogPost>
                );
              })}
            </MainContent>
            
            <BlogSidebar currentCategory={category} />
          </BlogPageContainer>
        </ContentPage>
    </SiteLayout>
  );
};

export const query = graphql`
  query CategoryPosts($category: String!) {
    allMarkdownRemark(
      sort: {fields: [frontmatter___date], order: DESC}
      filter: {frontmatter: {category: {eq: $category}}}
    ) {
      edges {
        node {
          excerpt(pruneLength: 120)
          fields {
            slug
          }
          frontmatter {
            date(formatString: "DD.MMMM.YYYY")
            title
            description
            thumbnail
            category
            tags
          }
        }
      }
    }
  }
`;

export default CategoryTemplate; 